import dayjs from "dayjs";

import type {
  GetPerformanceReportParams,
  GetPerformanceReportTab,
} from "../../api/generated";
import type { PerformanceReportTableParams, ViewTab } from "../types";

/**
 * The UI `ViewTab` and the API `tab` enum now share the same members
 * (`source | country | rebate | campaign`); the map doubles as a compile-time
 * check that they stay aligned.
 */
const API_TAB: Record<ViewTab, GetPerformanceReportTab> = {
  source: "source",
  country: "country",
  rebate: "rebate",
  campaign: "campaign",
};

/**
 * Campaign facet values are campaign ids as strings, plus this sentinel for a
 * source with no campaign attached (NULL campaign_id). It maps to the backend's
 * 0 sentinel — campaigns.id is a SERIAL from 1, so 0 never collides.
 */
export const NO_CAMPAIGN_FILTER_VALUE = "no-campaign";

export const toCampaignIds = (values: string[]): number[] =>
  values.map((value) =>
    value === NO_CAMPAIGN_FILTER_VALUE ? 0 : Number(value),
  );

/** Non-empty array or `undefined` (so empty filters are omitted from the query). */
export const orUndefined = <T>(values: T[]): T[] | undefined =>
  values.length > 0 ? values : undefined;

export const formatDate = (date: Date | null): string | undefined =>
  date ? dayjs(date).format("YYYY-MM-DD") : undefined;

/**
 * Maps the shared table params → the generated API query params for
 * `GET /v1/performance-report`.
 *
 * Shared rather than written per host because three of its rules are silent
 * when broken: the UI's singular filter keys become plural API keys with one
 * outright rename (`rebateProgram` → `rebatePrograms`), an empty filter must
 * drop out of the query entirely (the backend reads an absent param as "no
 * restriction"), and `sortDesc` is only meaningful alongside a `sortColumn`.
 * Every field is optional, so a divergent second copy would still typecheck.
 *
 * `toStatsQueryParams` (mapPerformanceStats.ts) is the stats-endpoint twin.
 */
export const toQueryParams = (
  params: PerformanceReportTableParams,
): GetPerformanceReportParams => ({
  tab: API_TAB[params.tab],
  startDate: formatDate(params.dateRange.startDate),
  endDate: formatDate(params.dateRange.endDate),
  sourceTypes: orUndefined(params.filters.sourceType),
  landingPages: orUndefined(params.filters.landingPage),
  bannerGroups: orUndefined(params.filters.bannerGroup),
  rebatePrograms: orUndefined(params.filters.rebateProgram),
  countries: orUndefined(params.filters.country),
  campaigns: orUndefined(toCampaignIds(params.filters.campaign)),
  sortColumn: params.sortColumn ?? undefined,
  sortDesc:
    params.sortColumn != null && params.sortDesc != null
      ? params.sortDesc
      : undefined,
  page: params.page,
  pageSize: params.pageSize,
});
